import { Button, OutlinedSelect, useMobile } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import DisplayError from '../../../components/Info/DisplayError';
import { TranslationsKeys } from '../../../locales/translations';
import { FormValues } from '../types';
import { lessonStatus } from '../utils';
import { useBaseLoader } from './BaseLoader';
import {
  ITEMS_PER_PAGE_DEFAULT,
  PAGE_INITIAL_DEFAULT,
  useTeacherAttendance,
} from './TeacherAttendanceLoader';

const DEFAULT_VALUES = {
  sectionId: null,
  classStatus: null,
};

export default function SearchControls() {
  const { t } = useTranslation(TranslationsKeys.TEACHER_ATTENDANCE_LIST);

  const isMobile = useMobile();
  const dictPrefix = 'filters';

  const {
    data: sections,
    loading: loadingSections,
    error,
    refresh,
  } = useBaseLoader();

  const { updateQuery, loading } = useTeacherAttendance();
  const { control, reset, handleSubmit } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const sectionsOptions = useMemo(() => {
    return (
      sections?.sections.map((section) => ({
        value: section.id,
        label: section.name,
      })) ?? []
    );
  }, [sections]);

  const handleClear = useCallback(() => {
    reset(DEFAULT_VALUES);
    updateQuery({
      sectionId: undefined,
      classStatus: undefined,
      items: ITEMS_PER_PAGE_DEFAULT,
      page: PAGE_INITIAL_DEFAULT,
    });
  }, [reset, updateQuery]);

  const handleSearch = useCallback(
    (values: FormValues) => {
      updateQuery({
        sectionId: values.sectionId?.value as number,
        classStatus: values.classStatus?.value as string,
        items: ITEMS_PER_PAGE_DEFAULT,
        page: PAGE_INITIAL_DEFAULT,
      });
    },
    [updateQuery],
  );

  if (error) {
    return (
      <DisplayError
        insideCard
        textBody={error.code}
        loadingAction={loading}
        retryAction={refresh}
      />
    );
  }

  return (
    <>
      <Form onSubmit={handleSubmit(handleSearch)}>
        <Row className="p-2">
          <Col xs={12} md={6} lg={3}>
            <OutlinedSelect
              name="sectionId"
              label={t(`${dictPrefix}.sectionId`)}
              placeholder={t(`${dictPrefix}.placeholderSection`)}
              control={control}
              options={sectionsOptions}
              isClearable={true}
              disabled={loading || loadingSections}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <OutlinedSelect
              name="classStatus"
              label={t(`${dictPrefix}.classStatus`)}
              placeholder={t(`${dictPrefix}.placeholderStatus`)}
              control={control}
              options={lessonStatus}
              isClearable={true}
              disabled={loading || loadingSections}
            />
          </Col>
          <Col
            xs={12}
            md={{ size: 3, offset: 6 }}
            lg={{ size: 2, offset: 2 }}
            style={{ marginTop: isMobile ? 0 : '1.5rem' }}
          >
            <Button
              text={t(`translation:common.actions.clean`)}
              size="md"
              fullwidth
              outlined
              onClick={handleClear}
              disabled={loading || loadingSections}
            />
          </Col>
          <Col
            xs={12}
            md={3}
            lg={2}
            style={{ marginTop: isMobile ? 0 : '1.5rem' }}
          >
            <Button
              type="submit"
              text={t(`translation:common.actions.search`)}
              size="md"
              fullwidth
              disabled={loading || loadingSections}
            />
          </Col>
        </Row>
      </Form>
      <div className="pl-2">
        <p className="fs-16 mt-3 mb-4">{t(`description`)}</p>
      </div>
    </>
  );
}
